import { cx } from "components/Graphs/utils";
import React from "react";

import ShellRailItem from "../ShellRailItem";
import ShellRailToggle from "../ShellRailToggle";
import type { ShellNavItem, ShellNavSection } from "../types";

export interface PrimaryIconRailProps {
  items: ShellNavItem[];
  sections?: ShellNavSection[];
  activeItemId: string;
  footerItems?: ShellNavItem[];
  ariaLabel?: string;
  onNavigate: (item: ShellNavItem) => void;
  mobile?: boolean;
  expanded?: boolean;
  collapsible?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  footer?: React.ReactNode;
  className?: string;
}

export const PrimaryIconRail: React.FC<PrimaryIconRailProps> = ({
  items,
  sections,
  activeItemId,
  footerItems = [],
  ariaLabel = "Product navigation",
  onNavigate,
  mobile = false,
  expanded = false,
  collapsible = false,
  onExpandedChange,
  footer,
  className,
}) => {
  const navigationId = React.useId();
  const hasExplicitSections = Boolean(sections);
  const visibleSections = (
    sections ?? [{ id: "primary", label: "", items }]
  ).filter((section) => section.items.length > 0);

  if (mobile) {
    const visibleItems = [
      ...visibleSections.flatMap((section) => section.items),
      ...footerItems,
    ];

    return (
      <nav
        aria-label={ariaLabel}
        data-shell-mobile-navigation
        className={cx(
          "flex h-full min-w-0 items-center gap-1 overflow-x-auto overflow-y-hidden border-t border-zinc-800 bg-[#090c0a]/[0.98] px-2 py-1.5 shadow-[0_-8px_28px_rgba(0,0,0,0.28)] backdrop-blur",
          className,
        )}
      >
        {visibleItems.map((item) => (
          <ShellRailItem
            key={item.id}
            item={item}
            active={item.id === activeItemId}
            onNavigate={onNavigate}
            mobile
          />
        ))}
        {footer ? <div className="shrink-0">{footer}</div> : null}
      </nav>
    );
  }

  return (
    <aside
      className={cx(
        "relative flex h-full min-h-0 w-full flex-col overflow-visible border-r border-zinc-800 bg-[#090c0a] px-0 py-4 shadow-[8px_0_28px_rgba(0,0,0,0.2)]",
        expanded ? "items-stretch" : "items-center",
        className,
      )}
      style={{
        backgroundImage:
          "linear-gradient(90deg, rgba(118,185,0,0.02), transparent 70%), repeating-linear-gradient(100deg, rgba(255,255,255,0.01) 0, rgba(255,255,255,0.01) 1px, transparent 1px, transparent 4px)",
      }}
    >
      <nav
        id={navigationId}
        aria-label={ariaLabel}
        data-shell-desktop-navigation
        className="flex min-h-0 w-full flex-1 flex-col"
      >
        <div className="min-h-0 w-full flex-1 overflow-y-auto overflow-x-hidden pb-3">
          {visibleSections.length > 0 ? (
            <div
              className={cx(
                "flex min-h-0 w-full flex-col gap-3",
                expanded ? "items-stretch" : "items-center",
              )}
            >
              {visibleSections.map((section, index) => {
                const labelId = hasExplicitSections
                  ? `${navigationId}-${section.id}`
                  : undefined;

                return (
                  <section
                    key={section.id}
                    aria-labelledby={labelId}
                    className={cx(
                      "flex w-full flex-col gap-1.5",
                      expanded ? "items-stretch" : "items-center",
                      index > 0 && "border-t border-zinc-800/80 pt-3",
                    )}
                  >
                    {hasExplicitSections ? (
                      <h2
                        id={labelId}
                        className={cx(
                          "px-4 text-[0.6875rem] font-semibold uppercase leading-5 tracking-[0.12em] text-zinc-500",
                          !expanded && "sr-only",
                        )}
                      >
                        {section.label}
                      </h2>
                    ) : null}
                    <div
                      className={cx(
                        "flex w-full flex-col gap-1.5",
                        expanded ? "items-stretch" : "items-center",
                      )}
                    >
                      {section.items.map((item) => (
                        <ShellRailItem
                          key={item.id}
                          item={item}
                          active={item.id === activeItemId}
                          onNavigate={onNavigate}
                          expanded={expanded}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          ) : (
            <span className="sr-only">No destinations available</span>
          )}
        </div>

        {footerItems.length > 0 ? (
          <div
            className={cx(
              "flex w-full shrink-0 flex-col gap-2 border-t border-zinc-800 pt-3",
              expanded ? "items-stretch" : "items-center",
            )}
          >
            {footerItems.map((item) => (
              <ShellRailItem
                key={item.id}
                item={item}
                active={item.id === activeItemId}
                onNavigate={onNavigate}
                expanded={expanded}
              />
            ))}
          </div>
        ) : null}
      </nav>

      {collapsible && onExpandedChange ? (
        <ShellRailToggle
          expanded={expanded}
          controlsId={navigationId}
          onToggle={() => onExpandedChange(!expanded)}
        />
      ) : null}

      {footer ? (
        <div
          className={cx(
            "w-full shrink-0 border-t border-zinc-800 pt-3",
            expanded ? "px-0" : "flex justify-center",
          )}
          data-shell-rail-footer
        >
          {footer}
        </div>
      ) : null}
    </aside>
  );
};

PrimaryIconRail.displayName = "PrimaryIconRail";

export default PrimaryIconRail;
